import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import apiClient from "../../AppClient";

const ProductModal = ({
  show,
  handleClose,
  mode,
  productData,
  refreshData,
}) => {
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    nodeCategory: "",
    classification: "",
    image: null,
    fertilization_schedule: "",
    schedule_type: "",
    quantity_available: "",
    sale_price: "",
    details: "",
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (productData) {
      setFormData({
        ...productData,
        quantity_number: productData.quantity_number || "",
        quantity_type: productData.quantity_type || "",
      });
    } else {
      setFormData({
        name: "",
        nodeCategory: "",
        classification: "",
        image: null,
        fertilization_schedule: "",
        schedule_type: "",
        quantity_number: "",
        quantity_type: "",
        sale_price: "",
        details: "",
      });
    }
  }, [productData]);
      

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get("/products/categories/");
        setCategories(response.data);
        setError(null);
      } catch (error) {
        setError(error.response?.data?.detail || "حدث خطأ أثناء الاتصال بالسيرفر");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: files ? files[0] : value,
    }));
    // Clear validation error for the field being updated
    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = "اسم المنتج مطلوب";
    if (!formData.nodeCategory) errors.nodeCategory = "نوع الصنف مطلوب";
    if (!formData.classification) errors.classification = "التصنيف مطلوب";
    if (!formData.sale_price) errors.sale_price = "السعر مطلوب";
    if (!formData.fertilization_schedule) errors.fertilization_schedule = "جدول التسميد مطلوب";
    if (!formData.schedule_type) errors.schedule_type = "نوع الجدول مطلوب";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  

  const handleAddProduct = async () => {
    const formDataObj = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataObj.append(key, value);
    });
    try {
      await apiClient.post("/products/create/", formDataObj);
      alert("تمت الإضافة بنجاح!");
      refreshData();
      handleClose();
    } catch (error) {
      setError(error.response?.data?.detail || "حدث خطأ أثناء الاتصال بالسيرفر");
    }
  };

  const handleUpdateProduct = async () => {
    const formDataObj = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataObj.append(key, value);
    });
    try {
      await apiClient.put(`/products/${productData.id}/update/`, formDataObj);
      alert("تم التعديل بنجاح!");
      refreshData();
      handleClose();
    } catch (error) {
      setError(error.response?.data?.detail || "حدث خطأ أثناء الاتصال بالسيرفر");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      if (mode === "add") {
        await handleAddProduct();
      } else {
        await handleUpdateProduct();
      }
    } catch (error) {
      // Error is already handled in handleAddProduct and handleUpdateProduct
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}  centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold">
          {mode === "add" ? "إضافة منتج جديد" : "تعديل المنتج"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="productName" className="mb-3">
            <Form.Label className="fw-bold">اسم المنتج</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="أدخل اسم المنتج"
              isInvalid={!!validationErrors.name}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="productNodeCategory" className="mb-3">
            <Form.Label className="fw-bold">نوع الصنف</Form.Label>
            <Form.Control
              as="select"
              name="nodeCategory"
              value={formData.nodeCategory}
              onChange={handleChange}
              isInvalid={!!validationErrors.nodeCategory}
            >
              <option value="" disabled>
                اختر النوع
              </option>
              {categories.map((cat) =>
                cat.subcategories.map((sub) =>
                  sub.nodecategories.map((node) => (
                    <option key={node.id} value={node.id}>
                      {cat.name}-{sub.name}-{node.name}
                    </option>
                  ))
                )
              )}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {validationErrors.nodeCategory}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="productClassification" className="mb-3">
            <Form.Label className="fw-bold">التصنيف</Form.Label>
            <Form.Control
              as="select"
              name="classification"
              value={formData.classification}
              onChange={handleChange}
              isInvalid={!!validationErrors.classification}
            >
              <option value="" disabled>
                اختر التصنيف
              </option>
              <option value="منتج">منتج</option>
              <option value="تم شراؤه">تم شراؤه</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {validationErrors.classification}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="productPrice" className="mb-3">
            <Form.Label className="fw-bold">السعر</Form.Label>
            <Form.Control
              type="number"
              name="sale_price"
              value={formData.sale_price}
              onChange={handleChange}
              placeholder="أدخل السعر"
              isInvalid={!!validationErrors.sale_price}
              dir="ltr"
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.sale_price}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="productImage" className="mb-3">
            <Form.Label className="fw-bold">تحميل الصورة</Form.Label>
            <Form.Control
              type="file"
              name="image"
              onChange={handleChange}
              accept="image/*"
            />
          </Form.Group>
          {/* imga preview */}
          {formData.image && (
            <div className="mb-3">
              <img
                src={formData.image instanceof File ? URL.createObjectURL(formData.image) : process.env.REACT_APP_API_URL + formData.image}
                alt="Preview"
                style={{ maxWidth: 250, height: "auto" }}
              />
            </div>
          )}

          <Form.Group controlId="productFertilization" className="mb-3">
            <Form.Label className="fw-bold">جدول التسميد</Form.Label>
            <div className="d-flex">
              <Form.Control
                type="number"
                name="fertilization_schedule"
                value={formData.fertilization_schedule}
                onChange={handleChange}
                placeholder="أدخل الرقم"
                isInvalid={!!validationErrors.fertilization_schedule}
                className="me-2"
              />
              <Form.Select
                name="schedule_type"
                value={formData.schedule_type}
                onChange={handleChange}
                isInvalid={!!validationErrors.schedule_type}
              >
                <option value="">اختر النوع</option>
                <option value="يوم">يوم</option>
                <option value="أسبوع">أسبوع</option>
                <option value="شهر">شهر</option>
                <option value="سنة">سنة</option>
              </Form.Select>
            </div>
            {validationErrors.fertilization_schedule && (
              <div className="invalid-feedback d-block">{validationErrors.fertilization_schedule}</div>
            )}
            {validationErrors.schedule_type && (
              <div className="invalid-feedback d-block">{validationErrors.schedule_type}</div>
            )}
          </Form.Group>

          <Form.Group controlId="productDesc" className="mb-3">
            <Form.Label className="fw-bold">الوصف</Form.Label>
            <Form.Control
              as="textarea"
              name="details"
              value={formData.details}
              onChange={handleChange}
              placeholder="وصف المنتج"
              rows={3}
            />
          </Form.Group>

          <Modal.Footer>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              className="fw-bold"
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  جاري التنفيذ...
                </>
              ) : mode === "add" ? (
                "إضافة"
              ) : (
                "تعديل"
              )}
            </Button>
            <Button variant="secondary" onClick={handleClose} className="fw-bold">
              إلغاء
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ProductModal;